import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useVoteSync } from '../contexts/VoteContext';
import { onAuthRequired } from '../utils/auth';
import { API_URLS } from '../constants';
import { PromptCardAuthor, PromptCardTags, PromptCardViews } from './ui/prompt';

interface PromptCardProps {
  id: string;
  title: string;
  description: string;
  votes: number;
  total_views: number;
  total_copies: number;
  promptType: string;
  tags?: string[];
  collectionIds?: number[];
  author_name?: string;
  author_profile_picture?: string;
}

const PromptCard: React.FC<PromptCardProps> = ({ id, title, description, votes, total_views, tags = [], author_name, author_profile_picture }) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { notifyVoteChange, subscribeToVoteChanges } = useVoteSync();
  const [voteCount, setVoteCount] = useState(votes);
  const [userVote, setUserVote] = useState<number | null>(null);
  const [votingInProgress, setVotingInProgress] = useState(false);
  const imagePlaceholder = author_name?.split(' ').map(item => item.charAt(0).toUpperCase()).join('') || 'AN';

  const formattedName = () => {
    if (!author_name) return;
    const parts = author_name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts?.length - 1][0].toUpperCase().trim()}`;
  };

  // Subscribe to vote changes from other components
  useEffect(() => {
    const unsubscribe = subscribeToVoteChanges(id, (voteUpdate) => {
      setVoteCount(voteUpdate.newVoteCount);
      setUserVote(voteUpdate.userVote);
    });

    return unsubscribe;
  }, [id, subscribeToVoteChanges]);

  const handleTitleClick = useCallback(() => {
    navigate(`/prompts/${id}`);
  }, [navigate, id]);

  const handleVote = useCallback(async (type: 'up' | 'down') => {
    if (votingInProgress) return;

    // Check authentication first
    if (!isLoggedIn) {
      onAuthRequired();
      return;
    }

    // Store current state for rollback on error
    const previousVoteCount = voteCount;
    const previousUserVote = userVote;

    setVotingInProgress(true);

    // Determine vote value based on current state and selected vote type
    let voteValue: number;

    // If clicking the same vote type that's already active, remove the vote (0)
    if ((userVote === 1 && type === 'up') || (userVote === -1 && type === 'down')) {
      voteValue = 0;
    } else {
      // Otherwise set the vote to up(1) or down(-1)
      voteValue = type === 'up' ? 1 : -1;
    }

    // Optimistic update
    let optimisticCount = voteCount;
    if (voteValue === 0) {
      // Removing vote
      optimisticCount = voteCount - (userVote || 0);
    } else if (userVote === null) {
      // Adding new vote
      optimisticCount = voteCount + voteValue;
    } else {
      // Changing vote (e.g., from upvote to downvote)
      optimisticCount = voteCount + (voteValue - userVote);
    }

    setVoteCount(optimisticCount);
    setUserVote(voteValue === 0 ? null : voteValue);

    try {
      // Use the API_URLS for backend requests
      const apiUrl = API_URLS.PROMPTS.VOTE(id);

      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include', // Include cookies for authentication
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ voteType: voteValue })
      });

      if (!response.ok) {
        throw new Error(`Failed to submit vote: ${response.statusText} (${response.status})`);
      }

      const result = await response.json();

      // Update state with server response (should match optimistic update)
      setVoteCount(result.currentVotes);
      setUserVote(result.userVote);

      // Notify other components of the vote change
      notifyVoteChange(id, result.currentVotes, result.userVote);
    } catch (err) {
      // Log detailed error and rollback optimistic update

      setVoteCount(previousVoteCount);
      setUserVote(previousUserVote);
    } finally {
      setVotingInProgress(false);
    }
  }, [votingInProgress, isLoggedIn, voteCount, userVote, id, notifyVoteChange]);

  const handleUpvote = useCallback(() => handleVote('up'), [handleVote]);
  const handleDownvote = useCallback(() => handleVote('down'), [handleVote]);

  return (
    <div className="bg-white rounded-lg shadow-md flex flex-col gap-3 px-6 py-4 w-full">
      {/* Header section with main title */}
      <div className="flex items-center">
        <h2 className="text-lg font-semibold text-brand-dark-blue leading-tight hover:text-brand-electric-blue transition-colors duration-200 cursor-pointer" onClick={handleTitleClick}>
          {title}
        </h2>
      </div>

        {/* Content paragraph */}
        <p className="text-sm text-left text-gray-600 leading-relaxed">
          {description}
        </p>

        {/* Bottom section with votes/views, tags, and author */}
        <div className="flex flex-wrap items-center justify-between border-t border-gray-200 pt-3 gap-4">
          {/* Left side: Vote count, Views, and Tags */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Vote count display */}
            <div className="flex items-center bg-brand-light-gray rounded-full px-3 py-1 text-brand-dark-blue text-sm font-medium">
              <span className="font-semibold">{voteCount} votes</span>
            </div>

            {/* Views with voting functionality */}
            <PromptCardViews
              total_views={total_views}
              userVote={userVote}
              votingInProgress={votingInProgress}
              handleDownvote={handleDownvote}
              handleUpvote={handleUpvote}
            />

            {/* Tags section */}
            <PromptCardTags tags={tags} />
          </div>

          {/* Right side: Author and Avatar */}
          {(author_name || author_profile_picture) && (
            <PromptCardAuthor
              author_name={author_name}
              author_profile_picture={author_profile_picture}
              imagePlaceholder={imagePlaceholder}
              formattedName={formattedName}
            />
          )}
        </div>
      </div>
  );
};

export default React.memo(PromptCard);
